import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

const TYPES = [
  { value: "browser", label: "Navegador (Playwright)" },
  { value: "rest_api", label: "API REST" },
  { value: "n8n_webhook", label: "Webhook n8n" },
];

export default function AddConnectionDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [type, setType] = useState("browser");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  function reset() {
    setName("");
    setUrl("");
    setType("browser");
    setNotes("");
  }

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !url.trim()) {
      toast.error("Nombre y URL son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/connections", {
        name: name.trim(),
        url: url.trim(),
        type,
        notes,
      });
      onCreated?.(data);
      toast.success("Conexión creada");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.connections.dialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[520px]"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Añadir conexión</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Registra un servicio web. Puedes activarlo más adelante.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conn-name" className="text-zinc-300">Nombre</Label>
            <Input
              id="conn-name"
              data-testid={TID.connections.fieldName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: ChatGPT Web"
              className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="conn-url" className="text-zinc-300">URL</Label>
            <Input
              id="conn-url"
              data-testid={TID.connections.fieldUrl}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400 font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Tipo de conexión</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                data-testid={TID.connections.fieldType}
                className="bg-zinc-900 border-zinc-800 text-zinc-100"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="focus:bg-zinc-900">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="conn-notes" className="text-zinc-300">Notas</Label>
            <Textarea
              id="conn-notes"
              data-testid={TID.connections.fieldNotes}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Detalles internos, autenticación, cadencia..."
              className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid={TID.connections.submit}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? "Guardando…" : "Crear conexión"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
